// src/pages/Attendance/MonthlySalary.tsx
import React, { useState, useEffect, useCallback, JSX } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Alert,
  Spinner,
  Input,
  Label,
  FormGroup,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from 'reactstrap';
import {
  RefreshCw,
  Calendar,
  AlertCircle,
  FileText,
  Download,
  Layers,
  Play,
  RotateCw,
  Clock,
  X,
  Sun,
  Award,
  Eye,
  ChevronDown,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { API_URL, apiClient } from '../../helpers/api_helper';
import { format } from 'date-fns';
import classnames from 'classnames';
import { fetchUnits, Unit } from 'api/unit';
import { createPortal } from 'react-dom';

// ─────────────────────────── types ───────────────────────────
interface Designation {
  id: number;
  title: string;
}
// ── Shared palette (used by table + floating dropdown) ──
const C = {
  slate: '#0f172a',
  muted: '#64748b',
  border: '#e5e7eb',
  borderStrong: '#cbd5e1',
  borderGroup: '#94a3b8',
  headBg: '#f1f5f9',
  headText: '#334155',
  groupBg: '#0f172a',
  stickyBg: '#ffffff',
  green: '#15803d',
  amber: '#b45309',
  red: '#b91c1c',
  blue: '#1d4ed8',
  purple: '#6d28d9',
};
interface AttendanceDayCell {
  hours: number | null;
  regular_hours: number | null;
  overtime_hours: number | null;
  status: string | null;
  is_holiday: boolean;
  is_sunday: boolean;
  is_overtime: boolean;
  is_manual: boolean;
  check_in: string | null;
  check_out: string | null;
  check_in_notes: string | null;
  check_out_notes: string | null;
  holiday_name?: string | null;
  lop?: {
    id: number;
    notes: string;
    date: string;
  } | null;
  sessions?: Array<{
    in: string;
    out: string;
    duration: number;
  }>;
  is_leave?: boolean;
  is_paid_leave?: boolean | null;
  is_half_day?: boolean;
  leave_type_id?: number | null;
  leave_type_name?: string | null;
  leave_reason?: string | null;
}

interface PayableBreakdown {
  total_calendar_days: number;
  sundays_paid: number;
  sundays_lop: number;
  holidays_paid: number;
  holidays_lop: number;
  regular_weekday_weight: number;
  lop_days_deducted: number;
  total_payable_days: number;
}

interface Payslip {
  id: number;
  employee_id: number;
  year: number;
  month: number;
  employee_code: string;
  employee_name: string;
  designation_title: string;
  unit_name: string;
  // Days
  calendar_days: number;
  working_days: number;
  days_worked: number;
  sundays_paid: number;
  sundays_lop: number;
  sundays_worked: number;
  holidays_paid: number;
  holidays_lop: number;
  holidays_worked: number;
  lop_days: number;
  absent_days: number;
  half_days: number | string;

  // Earnings
  basic_salary: number;
  take_home_salary: number;
  hra: number;
  travel_allowance: number;
  special_allowance: number;
  site_allowance: number;
  per_day_salary: number;
  gross_salary: number;

  // Statutory
  epf_wage: number;
  epf_employee: number;
  epf_employer: number;
  esi_employee: number;
  esi_employer: number;
  total_employee_contribution: number;
  total_employer_contribution: number;
  professional_tax: number;
  net_salary: number;
  ctc: number;
  extra_days: number;
  extra_pay: number;
  extra_pay_sundays?: number;
  extra_pay_holidays?: number;

  // ✅ Offer values snapshotted on the payslip
  original_take_home_salary?: number | null;
  original_basic_salary?: number | null;
  original_site_allowance?: number | null;
  // Rates snapshot
  rates_epf_employee_pct: number;
  rates_epf_employer_pct: number;
  rates_esi_employee_pct: number;
  rates_esi_employer_pct: number;
  rates_epf_wage_ceiling: number;
  rates_esi_gross_ceiling: number;
  rates_effective_from: string;

  // JSON blobs
  breakdown?: Record<string, AttendanceDayCell> | null;
  payable_breakdown?: PayableBreakdown | null;

  generated_at: string;
  updated_at: string;
}

interface PayslipListResponse {
  success: boolean;
  data: {
    year: number;
    month: number;
    count: number;
    employees: Payslip[];
  };
}

interface PayslipStatusResponse {
  success: boolean;
  data: {
    year: number;
    month: number;
    generated: boolean;
    count: number;
    last_generated_at: string | null;
  };
}

interface GenerateResponse {
  success: boolean;
  message: string;
  data: {
    year: number;
    month: number;
    created: number;
    updated: number;
    skipped: number;
    total: number;
    warnings?: {
      code: string;
      message: string;
      count: number;
      details: MissingCheckoutRow[];
    };
  };
}

interface MissingCheckoutRow {
  employee_id: number;
  employee_code: string;
  employee_name: string;
  date: string;
  day: number;
  check_in: string | null;
  status: string | null;
}

type TabType = 'general';

// ─────────────────────────── helpers ───────────────────────────
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: format(new Date(2000, i, 1), 'MMMM'),
}));

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 11 }, (_, i) => ({
  value: currentYear - 5 + i,
  label: (currentYear - 5 + i).toString(),
}));

const formatCurrency = (amount: number | string | null | undefined): string => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return (
    '₹' +
    (n || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  return typeof v === 'string' ? parseFloat(v) || 0 : v;
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatTo12Hour = (timeStr?: string | null): string => {
  if (!timeStr) return '—';
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  if (isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  const hh = hr < 10 ? '0' + hr : String(hr);
  return `${hh}:${m} ${ampm}`;
};

function getDayWeight(
  status: string | null,
  isSunday: boolean,
  isHoliday: boolean,
  isLop: boolean,
  isLeave: boolean = false,
  isPaidLeave: boolean | null = null,
  isHalfDayLeave: boolean = false,
): number {
  // LOP → 0, overrides everything
  if (isLop) return 0;

  // Unpaid leave → behaves like LOP
  if (isLeave && !isPaidLeave) return 0;

  // Paid leave on a weekday → 1.0 or 0.5
  if (isLeave && isPaidLeave) return isHalfDayLeave ? 0.5 : 1.0;

  // Sunday / Holiday base paid leave → 1.0
  if (isSunday || isHoliday) return 1.0;

  // Weekday
  if (status === 'full_day') return 1.0;
  if (status === 'half_day' || status === 'short_day') return 0.5;
  if (status === 'absent' || status === 'no_out_time') return 0;
  if (status === 'leave_full_day') return 1.0;
  if (status === 'leave_half_day') return 0.5;

  return 0;
}

function getStatusLabel(status: string | null, cell?: any): string {
  if (cell?.is_leave) {
    const name = cell.leave_type_name ?? 'Leave';
    return cell.is_half_day ? `${name} (Half)` : name;
  }
  switch (status) {
    case 'full_day': return 'Full Day';
    case 'half_day': return 'Half Day';
    case 'short_day': return 'Short Day';
    case 'absent': return 'Absent';
    case 'no_out_time': return 'No Out Time';
    case 'sunday': return 'Sunday';
    case 'leave_full_day': return 'Leave (Full)';
    case 'leave_half_day': return 'Leave (Half)';
    default: return status ?? '—';
  }
}

function getStatusColor(status: string | null, cell?: any): string {
  if (cell?.is_leave) {
    return cell.is_paid_leave ? 'info' : 'warning';
  }
  switch (status) {
    case 'full_day': return 'success';
    case 'half_day':
    case 'short_day': return 'warning';
    case 'absent':
    case 'no_out_time': return 'danger';
    case 'sunday': return 'secondary';
    case 'leave_full_day':
    case 'leave_half_day': return 'info';
    default: return 'secondary';
  }
}

// ─────────────────────────── page ───────────────────────────
const MonthlySalary: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('general');

  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [status, setStatus] = useState<PayslipStatusResponse['data'] | null>(null);
  const [error, setError] = useState('');

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [designations, setDesignations] = useState<Designation[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [filters, setFilters] = useState({
    designation_id: undefined as number | undefined,
    unit_id: undefined as number | undefined,
  });
  const [payslipDropdownId, setPayslipDropdownId] = useState<number | null>(null);
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);

  const [payslipLoadingId, setPayslipLoadingId] = useState<number | null>(null);
  // ── payslip detail modal ──
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPayslip, setDetailPayslip] = useState<Payslip | null>(null);

  // ── Missing check-out modal ──
  const [missingModal, setMissingModal] = useState(false);
  const [missingRows, setMissingRows] = useState<MissingCheckoutRow[]>([]);

  const openDetail = (p: Payslip) => {
    setDetailPayslip(p);
    setDetailOpen(true);
  };
  const closeDetail = () => {
    setDetailOpen(false);
    setDetailPayslip(null);
  };
  const openDropdown = (id: number, e: React.MouseEvent<HTMLButtonElement>) => {
    if (payslipDropdownId === id) {
      setPayslipDropdownId(null);
      setDropdownRect(null);
      return;
    }
    setPayslipDropdownId(id);
    setDropdownRect(e.currentTarget.getBoundingClientRect());
  };

  const closeDropdown = () => {
    setPayslipDropdownId(null);
    setDropdownRect(null);
  };

  useEffect(() => {
    if (payslipDropdownId === null) return;

    const onDocClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      // Ignore clicks inside the dropdown menu itself
      if (target.closest('[data-payslip-dropdown]')) return;
      // Ignore clicks on the trigger button
      if (target.closest('[data-payslip-trigger]')) return;
      closeDropdown();
    };

    const onScrollOrResize = () => closeDropdown();
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('keydown', onKey);
    };
  }, [payslipDropdownId]);
  // ── fetch status ──
  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await apiClient.get('/payslip/status', {
        year: selectedYear,
        month: selectedMonth,
      });
      if (res?.success) setStatus(res.data);
    } catch (err) {
      console.error('Error fetching payslip status:', err);
    } finally {
      setStatusLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  // ── fetch payslips ──
  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { year: selectedYear, month: selectedMonth };
      if (filters.designation_id) params.designation_id = filters.designation_id;
      if (filters.unit_id) params.unit_id = filters.unit_id;

      const res: PayslipListResponse = await apiClient.get('/payslip', params);
      if (res?.success) {
        console.log('data', res.data)
        setPayslips(res.data.employees || []);
      } else {
        setError((res as any)?.message || 'Failed to fetch payslips');
        toast.error('Failed to fetch payslips');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch payslips');
      toast.error(err.message || 'Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, filters]);


  const handlePayslipPdf = async (
    employeeId: number,
    year: number,
    month: number,
    mode: 'view' | 'download',
  ) => {
    setPayslipDropdownId(null);
    setPayslipLoadingId(employeeId);

    const toastId = toast.loading(
      mode === 'view' ? 'Preparing payslip…' : 'Preparing download…',
    );

    const token = localStorage.getItem('accessToken');
    const url = `${API_URL}/payslips/${employeeId}/${year}/${month}/pdf`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/pdf',
        },
      });

      if (!response.ok) {
        // Try to parse a JSON error body (backend may return JSON on failure)
        let msg = `Failed to generate payslip PDF (status ${response.status})`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const j = await response.json();
            if (j?.message) msg = j.message;
          } else {
            const text = await response.text();
            if (text) msg = text.slice(0, 200);
          }
        } catch {
          /* ignore */
        }

        if (response.status === 401) {
          msg = 'Session expired. Please login again.';
          // Let the app's existing 401 handler redirect. Or you can redirect here:
          // window.location.href = '/login';
        }

        throw new Error(msg);
      }

      const blob = await response.blob();
      console.log('📄 [PAYSLIP PDF] blob:', {
        size: blob.size,
        type: blob.type,
      });

      if (!blob || blob.size === 0) {
        throw new Error('Empty PDF received');
      }

      const blobUrl = URL.createObjectURL(blob);
      console.log('📄 [PAYSLIP PDF] blobUrl created:', blobUrl);

      if (mode === 'view') {
        console.log('📄 [PAYSLIP PDF] opening in new tab…');
        const newWindow = window.open(blobUrl, '_blank');
        console.log('📄 [PAYSLIP PDF] window.open returned:', newWindow);

        if (!newWindow) {
          toast.update(toastId, {
            render: 'Popup blocked — please allow popups to view the payslip',
            type: 'warning',
            isLoading: false,
            autoClose: 4000,
          });
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        console.log('📄 [PAYSLIP PDF] triggering download…');
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Payslip_${employeeId}_${year}_${String(month).padStart(2, '0')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
        console.log('📄 [PAYSLIP PDF] download triggered');
      }

      toast.update(toastId, {
        render: mode === 'view' ? 'Payslip opened' : 'Payslip downloaded',
        type: 'success',
        isLoading: false,
        autoClose: 2000,
      });

    } catch (err: any) {
      toast.update(toastId, {
        render: err?.message || 'Failed to generate payslip PDF',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setPayslipLoadingId(null);
    }
  };


  const fetchDesignations = useCallback(async () => {
    try {
      const res = await apiClient.get('/designations');
      if (res?.data) setDesignations(res.data);
    } catch (err) {
      console.error('Error fetching designations:', err);
    }
  }, []);

  const fetchUnitsData = useCallback(async () => {
    setUnitsLoading(true);
    try {
      const unitsData = await fetchUnits();
      setUnits(unitsData || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      setUnitsLoading(false);
    }
  }, []);
  const refresh = useCallback(async () => {
    await fetchStatus();
    await fetchPayslips();
  }, [fetchStatus, fetchPayslips]);

  useEffect(() => {
    fetchDesignations();
    fetchUnitsData();
  }, [fetchDesignations, fetchUnitsData]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, activeTab]);

  const handleFilterChange = (key: string, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () =>
    setFilters({ designation_id: undefined, unit_id: undefined });

  // ── generate / regenerate ──
  const handleGenerate = async () => {
    if (!status?.generated) {
      const ok = window.confirm(
        `Generate payslips for ${format(
          new Date(selectedYear, selectedMonth - 1, 1),
          'MMMM yyyy',
        )}?`,
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        `Regenerate payslips for ${format(
          new Date(selectedYear, selectedMonth - 1, 1),
          'MMMM yyyy',
        )}?\n\nThis will OVERWRITE the existing ${status.count} payslip(s) with fresh values.`,
      );
      if (!ok) return;
    }

    setGenerating(true);
    try {
      const params: any = { year: selectedYear, month: selectedMonth };
      if (filters.designation_id) params.designation_id = filters.designation_id;
      if (filters.unit_id !== undefined) params.unit_id = filters.unit_id;

      const res: GenerateResponse = await apiClient.post(
        '/payslip/generate',
        {},
        { params },
      );

      if (res?.success) {
        // ✅ Warnings surface now come from the 200 response, not the catch
        if (res?.data?.warnings?.details?.length) {
          toast.warning(res.message || 'Payslips generated with warnings');
          setMissingRows(res.data.warnings.details);
          setMissingModal(true);
        } else {
          toast.success(res.message || 'Payslips generated');
        }
        await refresh();
      } else {
        toast.error((res as any)?.message || 'Failed to generate payslips');
      }
    } catch (err: any) {
      console.error(err);
      const errData = err?.response?.data || err?.data;

      // Fallback in case backend still throws (older flow)
      if (
        errData?.code === 'MISSING_CHECKOUT' &&
        Array.isArray(errData?.details)
      ) {
        setMissingRows(errData.details);
        setMissingModal(true);
        return;
      }

      toast.error(
        errData?.message || err?.message || 'Failed to generate payslips',
      );
    } finally {
      setGenerating(false);
    }
  };

  // ── export excel ──
  const handleExportExcel = async () => {
    if (payslips.length === 0) {
      toast.warning('No payslips to export. Generate first.');
      return;
    }
    try {
      setExporting(true);

      const params = new URLSearchParams({
        year: selectedYear.toString(),
        month: selectedMonth.toString(),
      });
      if (filters.designation_id)
        params.append('designation_id', filters.designation_id.toString());
      if (filters.unit_id !== undefined && filters.unit_id !== null)
        params.append('unit_id', filters.unit_id.toString());

      const endpoint =
        activeTab === 'general'
          ? '/salary/export/general'
          : '/salary/export/combined';

      const token = localStorage.getItem('accessToken');
      const url = `${API_URL}${endpoint}?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!response.ok) {
        let msg = 'Export failed';
        try {
          const j = await response.json();
          msg = j.message || msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Received empty file');

      const monthLabel = format(
        new Date(selectedYear, selectedMonth - 1, 1),
        'MMMM_yyyy',
      );
      const filename = `Salary_${monthLabel}.xlsx`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 100);

      toast.success('Salary exported successfully!');
    } catch (err: any) {
      console.error('Export error:', err);
      toast.error(err.message || 'Failed to export salary');
    } finally {
      setExporting(false);
    }
  };

  // ── table ──
  const renderTable = () => {
    if (!status?.generated) {
      return (
        <div className="text-center py-5">
          <Calendar size={48} className="text-muted mb-3" />
          <h5>No payslips generated</h5>
          <p className="text-muted">
            Payslips for{' '}
            {format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}{' '}
            haven't been generated yet.
          </p>
          <Button color="primary" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <>
                <Spinner size="sm" className="me-1" /> Generating…
              </>
            ) : (
              <>
                <Play size={14} className="me-1" /> Generate Payslips
              </>
            )}
          </Button>
        </div>
      );
    }

    if (payslips.length === 0) {
      return (
        <div className="text-center py-5">
          <Calendar size={48} className="text-muted mb-3" />
          <h5>No records found</h5>
          <p className="text-muted">No payslips match the current filters.</p>
        </div>
      );
    }

    // ── Totals ──
    const totals = payslips.reduce(
      (acc, p) => ({
        days_worked: acc.days_worked + toNum(p.days_worked),
        extra_days: acc.extra_days + toNum(p.extra_days),
        extra_pay: acc.extra_pay + toNum(p.extra_pay),
        per_day_basic: acc.per_day_basic + toNum(p.per_day_salary),
        basic_earned: acc.basic_earned + toNum(p.basic_salary),
        hra: acc.hra + toNum(p.hra),
        travel: acc.travel + toNum(p.travel_allowance),
        special: acc.special + toNum(p.special_allowance),
        site: acc.site + toNum(p.site_allowance),
        gross: acc.gross + toNum(p.gross_salary),
        epf_employee: acc.epf_employee + toNum(p.epf_employee),
        esi_employee: acc.esi_employee + toNum(p.esi_employee),
        professional_tax: acc.professional_tax + toNum(p.professional_tax),
        epf_employer: acc.epf_employer + toNum(p.epf_employer),   // ✅ NEW
        esi_employer: acc.esi_employer + toNum(p.esi_employer),   // ✅ NEW
        net: acc.net + toNum(p.net_salary),
        ctc: acc.ctc + toNum(p.ctc),
        lop_days: acc.lop_days + toNum(p.lop_days),
      }),
      {
        days_worked: 0,
        extra_days: 0,
        extra_pay: 0,
        per_day_basic: 0,
        basic_earned: 0,
        hra: 0,
        travel: 0,
        special: 0,
        site: 0,
        gross: 0,
        epf_employee: 0,
        esi_employee: 0,
        professional_tax: 0,
        epf_employer: 0,   // ✅ NEW
        esi_employer: 0,   // ✅ NEW
        net: 0,
        ctc: 0,
        lop_days: 0,
      },
    );

    // ── Palette ──
    const C = {
      slate: '#0f172a',
      muted: '#64748b',
      border: '#e5e7eb',
      borderStrong: '#cbd5e1',
      borderGroup: '#94a3b8',
      headBg: '#f1f5f9',
      headText: '#334155',
      groupBg: '#0f172a',
      stickyBg: '#ffffff',
      green: '#15803d',
      amber: '#b45309',
      red: '#b91c1c',
      blue: '#1d4ed8',
      purple: '#6d28d9',
      teal: '#0e7490',      // ✅ NEW — for employer contributions
    };

    // ── Column widths ──
    const W = {
      sno: 55,
      code: 120,
      name: 260,
      paidDays: 90,
      lop: 70,
      extraDays: 100,
      perDay: 95,
      basic: 105,
      hra: 90,
      travel: 95,
      special: 100,
      site: 80,
      gross: 110,
      epf: 95,
      esi: 95,
      ptax: 95,
      epfEmployer: 110,   // ✅ NEW
      esiEmployer: 110,   // ✅ NEW
      net: 110,
      extraPay: 110,
      ctc: 115,
    };

    const LEFT = {
      sno: 0,
      code: W.sno,
      name: W.sno + W.code,
    };

    const totalWidth =
      W.sno + W.code + W.name + W.paidDays + W.lop + W.extraDays +
      W.perDay + W.basic + W.hra + W.travel + W.special + W.site +
      W.gross + W.epf + W.esi + W.ptax + W.epfEmployer + W.esiEmployer +
      W.net + W.extraPay + W.ctc;

    // ── Borders ──
    const colBorder = `1px solid ${C.border}`;
    const colBorderStrong = `1px solid ${C.borderStrong}`;
    const groupBorderLeft = `2px solid ${C.borderGroup}`;

    // ── Base cell styles ──
    const thBase: React.CSSProperties = {
      padding: '10px 8px',
      fontSize: '0.72rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: C.headText,
      backgroundColor: C.headBg,
      borderBottom: `2px solid ${C.border}`,
      borderRight: colBorder,
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      boxSizing: 'border-box',
    };

    const thGroup: React.CSSProperties = {
      ...thBase,
      backgroundColor: C.groupBg,
      color: '#fff',
      fontSize: '0.68rem',
      letterSpacing: '0.06em',
      textAlign: 'center',
      padding: '6px 8px',
      borderRight: '1px solid rgba(255,255,255,0.18)',
    };

    const tdBase: React.CSSProperties = {
      padding: '10px 8px',
      fontSize: '0.82rem',
      color: C.slate,
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      borderRight: colBorder,
      boxSizing: 'border-box',
    };

    const tdNum: React.CSSProperties = {
      ...tdBase,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    };

    const tdNumBold: React.CSSProperties = {
      ...tdNum,
      fontWeight: 700,
    };

    const tdCenter: React.CSSProperties = {
      ...tdBase,
      textAlign: 'center',
    };

    const tdGroupStart: React.CSSProperties = { borderLeft: groupBorderLeft };

    const stickyCell = (
      left: number,
      width: number,
      opts?: { strong?: boolean; align?: 'left' | 'center' },
    ): React.CSSProperties => ({
      ...tdBase,
      position: 'sticky',
      left,
      zIndex: 2,
      backgroundColor: C.stickyBg,
      boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
      textAlign: opts?.align ?? 'left',
      fontWeight: opts?.strong ? 700 : 400,
      width,
      minWidth: width,
      maxWidth: width,
      overflow: 'hidden',
    });

    const stickyHeader = (
      left: number,
      width: number,
      opts?: { align?: 'left' | 'center' },
    ): React.CSSProperties => ({
      ...thBase,
      position: 'sticky',
      left,
      zIndex: 4,
      backgroundColor: C.headBg,
      boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
      textAlign: opts?.align ?? 'left',
      width,
      minWidth: width,
      maxWidth: width,
      overflow: 'hidden',
    });

    return (
      <div
        className="salary-scroll"
        style={{
          maxHeight: '70vh',
          overflow: 'auto',
          border: `1px solid ${C.border}`,
          borderRadius: 8,
        }}
      >
        <table
          className="mb-0"
          style={{
            fontSize: '0.85rem',
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: totalWidth,
            minWidth: totalWidth,
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: W.sno }} />
            <col style={{ width: W.code }} />
            <col style={{ width: W.name }} />
            <col style={{ width: W.paidDays }} />
            <col style={{ width: W.lop }} />
            <col style={{ width: W.extraDays }} />
            <col style={{ width: W.perDay }} />
            <col style={{ width: W.basic }} />
            <col style={{ width: W.hra }} />
            <col style={{ width: W.travel }} />
            <col style={{ width: W.special }} />
            <col style={{ width: W.site }} />
            <col style={{ width: W.gross }} />
            <col style={{ width: W.epf }} />
            <col style={{ width: W.esi }} />
            <col style={{ width: W.ptax }} />
            <col style={{ width: W.epfEmployer }} />
            <col style={{ width: W.esiEmployer }} />
            <col style={{ width: W.net }} />
            <col style={{ width: W.extraPay }} />
            <col style={{ width: W.ctc }} />
          </colgroup>

          <thead>
            {/* Group header row */}
            <tr>
              <th
                style={{ ...stickyHeader(LEFT.sno, W.sno, { align: 'center' }) }}
                rowSpan={2}
              >
                #
              </th>
              <th
                style={{ ...stickyHeader(LEFT.code, W.code, { align: 'center' }) }}
                rowSpan={2}
              >
                Emp Code
              </th>
              <th
                style={{ ...stickyHeader(LEFT.name, W.name, { align: 'left' }) }}
                rowSpan={2}
              >
                Employee
              </th>

              <th style={thGroup} colSpan={3}>
                Attendance
              </th>

              <th style={thGroup} colSpan={7}>
                Earnings (₹)
              </th>

              <th style={thGroup} colSpan={3}>
                Deductions (Employee)
              </th>

              <th style={thGroup} colSpan={2}>
                Contributions (Employer)
              </th>

              <th style={thGroup} colSpan={3}>
                Totals (₹)
              </th>
            </tr>

            {/* Column header row */}
            <tr>
              <th style={{ ...thBase, textAlign: 'center' }}>Paid Days</th>
              <th style={{ ...thBase, textAlign: 'center' }}>LOP</th>
              <th style={{ ...thBase, textAlign: 'center' }}>Extra Days</th>

              <th style={{ ...thBase, textAlign: 'right' }}>Per Day</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Basic</th>
              <th style={{ ...thBase, textAlign: 'right' }}>HRA</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Travel</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Special</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Site</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Gross</th>

              <th style={{ ...thBase, textAlign: 'right', ...tdGroupStart }}>EPF</th>
              <th style={{ ...thBase, textAlign: 'right' }}>ESI</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Prof. Tax</th>

              <th style={{ ...thBase, textAlign: 'right', ...tdGroupStart }}>EPF (Er)</th>
              <th style={{ ...thBase, textAlign: 'right' }}>ESI (Er)</th>

              <th style={{ ...thBase, textAlign: 'right', ...tdGroupStart }}>Net</th>
              <th style={{ ...thBase, textAlign: 'right' }}>Extra Pay</th>
              <th style={{ ...thBase, textAlign: 'right' }}>CTC</th>
            </tr>
          </thead>

          <tbody>
            {payslips.map((p, index) => {
              const lopDays = toNum(p.lop_days);
              const daysWorked = toNum(p.days_worked);
              const workingDays = toNum(p.working_days);
              const extraDaysWorked =
                toNum(p.sundays_worked) + toNum(p.holidays_worked);
              const extraPay = toNum(p.extra_pay);

              const offerTakeHome =
                toNum((p as any).original_take_home_salary) ||
                toNum(p.take_home_salary);
              const offerBasic =
                toNum((p as any).original_basic_salary) || toNum(p.basic_salary);
              const offerSite =
                toNum((p as any).original_site_allowance) ||
                toNum(p.site_allowance);

              return (
                <tr key={p.id} className="salary-row">
                  <td style={{ ...stickyCell(LEFT.sno, W.sno, { align: 'center' }) }}>
                    {index + 1}
                  </td>

                  <td
                    style={{
                      ...stickyCell(LEFT.code, W.code, { align: 'center' }),
                    }}
                  >
                    {payslipLoadingId === p.employee_id ? (
                      <Spinner size="sm" color="primary" />
                    ) : (
                      <button
                        type="button"
                        data-payslip-trigger
                        className="btn btn-sm d-flex align-items-center justify-content-center gap-1"
                        style={{
                          backgroundColor: '#4b1ac9',
                          color: '#fff',
                          border: 'none',
                          fontSize: '0.72rem',
                          padding: '4px 8px',
                          borderRadius: 6,
                          fontWeight: 600,
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          margin: '0 auto',
                          boxSizing: 'border-box',
                        }}
                        onClick={(e) => openDropdown(p.id, e)}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flexShrink: 1,
                          }}
                        >
                          {p.employee_code}
                        </span>
                        <ChevronDown
                          size={12}
                          style={{
                            flexShrink: 0,
                            transition: 'transform 0.15s',
                            transform:
                              payslipDropdownId === p.id
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                          }}
                        />
                      </button>
                    )}
                  </td>

                  <td
                    style={{
                      ...stickyCell(LEFT.name, W.name, { align: 'left' }),
                      whiteSpace: 'normal',
                    }}
                  >
                    <div
                      className="d-flex flex-column"
                      style={{ lineHeight: 1.45, gap: 2, minWidth: 0 }}
                    >
                      <span
                        className="fw-semibold"
                        style={{
                          fontSize: '0.9rem',
                          color: C.slate,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={p.employee_name}
                      >
                        {p.employee_name}
                      </span>

                      <small
                        style={{
                          fontSize: '0.68rem',
                          color: C.muted,
                          display: 'inline-flex',
                          gap: 10,
                          flexWrap: 'wrap',
                          marginTop: 2,
                        }}
                        title="Offer values from employee master"
                      >
                        <span>
                          Offer TH:{' '}
                          <span style={{ color: C.slate, fontWeight: 600 }}>
                            ₹{offerTakeHome.toLocaleString('en-IN')}
                          </span>
                        </span>
                        <span>
                          Basic:{' '}
                          <span style={{ color: C.slate, fontWeight: 600 }}>
                            ₹{offerBasic.toLocaleString('en-IN')}
                          </span>
                        </span>
                        {offerSite > 0 && (
                          <span>
                            Site:{' '}
                            <span style={{ color: C.slate, fontWeight: 600 }}>
                              ₹{offerSite.toLocaleString('en-IN')}
                            </span>
                          </span>
                        )}
                      </small>
                    </div>
                  </td>

                  {/* Attendance */}
                  <td style={tdCenter}>
                    <span
                      style={{
                        backgroundColor: C.blue,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        padding: '3px 9px',
                        borderRadius: 999,
                        display: 'inline-block',
                        minWidth: 40,
                      }}
                    >
                      {daysWorked.toFixed(1)}
                    </span>
                    <small
                      className="d-block"
                      style={{ fontSize: '0.65rem', color: C.muted, marginTop: 2 }}
                    >
                      of {workingDays}
                    </small>
                  </td>

                  <td style={tdCenter}>
                    {lopDays > 0 ? (
                      <span
                        style={{
                          backgroundColor: C.red,
                          color: '#fff',
                          border: '1.5px solid #facc15',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          padding: '3px 9px',
                          borderRadius: 999,
                          display: 'inline-block',
                          minWidth: 30,
                        }}
                      >
                        {lopDays}
                      </span>
                    ) : (
                      <span style={{ color: C.muted }}>—</span>
                    )}
                  </td>

                  <td style={tdCenter}>
                    {extraDaysWorked > 0 ? (
                      <span
                        style={{
                          backgroundColor: C.purple,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          padding: '3px 9px',
                          borderRadius: 999,
                          display: 'inline-block',
                          minWidth: 30,
                        }}
                        title="Sundays + Holidays worked"
                      >
                        {extraDaysWorked}
                      </span>
                    ) : (
                      <span style={{ color: C.muted }}>—</span>
                    )}
                  </td>

                  {/* Earnings */}
                  <td style={tdNum}>{formatCurrency(p.per_day_salary)}</td>
                  <td style={tdNumBold}>{formatCurrency(p.basic_salary)}</td>
                  <td style={tdNum}>{formatCurrency(p.hra)}</td>
                  <td style={tdNum}>{formatCurrency(p.travel_allowance)}</td>
                  <td style={tdNum}>{formatCurrency(p.special_allowance)}</td>
                  <td style={tdNum}>{formatCurrency(p.site_allowance)}</td>
                  <td style={{ ...tdNumBold, color: C.slate }}>
                    {formatCurrency(p.gross_salary)}
                  </td>

                  {/* Deductions (Employee) */}
                  <td style={{ ...tdNum, color: C.red, ...tdGroupStart }}>
                    {formatCurrency(p.epf_employee)}
                  </td>
                  <td style={{ ...tdNum, color: C.red }}>
                    {formatCurrency(p.esi_employee)}
                  </td>
                  <td style={{ ...tdNum, color: C.red }}>
                    {formatCurrency(p.professional_tax)}
                  </td>

                  {/* Contributions (Employer) — NEW */}
                  <td
                    style={{
                      ...tdNum,
                      color: C.teal,
                      ...tdGroupStart,
                    }}
                  >
                    {formatCurrency(p.epf_employer)}
                  </td>
                  <td style={{ ...tdNum, color: C.teal }}>
                    {formatCurrency(p.esi_employer)}
                  </td>

                  {/* Totals */}
                  <td style={{ ...tdNumBold, color: C.green, ...tdGroupStart }}>
                    {formatCurrency(p.net_salary)}
                  </td>
                  <td style={{ ...tdNum, color: C.purple }}>
                    {extraPay > 0 ? formatCurrency(extraPay) : '—'}
                  </td>
                  <td style={{ ...tdNumBold, color: C.blue }}>
                    {formatCurrency(p.ctc)}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr
              style={{
                backgroundColor: '#e2e8f0',
                color: C.slate,
                fontWeight: 700,
              }}
            >
              <td
                colSpan={3}
                style={{
                  ...stickyCell(LEFT.sno, W.sno + W.code + W.name, {
                    strong: true,
                  }),
                  backgroundColor: '#e2e8f0',
                }}
              >
                TOTAL ({payslips.length} employees)
              </td>

              <td style={{ ...tdCenter, fontWeight: 700, color: C.blue }}>
                {totals.days_worked.toFixed(1)}
              </td>
              <td style={{ ...tdCenter, fontWeight: 700, color: C.red }}>
                {totals.lop_days > 0 ? totals.lop_days : '—'}
              </td>
              <td style={{ ...tdCenter, fontWeight: 700, color: C.purple }}>
                {totals.extra_days > 0 ? totals.extra_days : '—'}
              </td>

              <td style={tdNumBold}>{formatCurrency(totals.per_day_basic)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.basic_earned)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.hra)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.travel)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.special)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.site)}</td>
              <td style={tdNumBold}>{formatCurrency(totals.gross)}</td>

              {/* Deductions (Employee) */}
              <td style={{ ...tdNumBold, color: C.red, ...tdGroupStart }}>
                {formatCurrency(totals.epf_employee)}
              </td>
              <td style={{ ...tdNumBold, color: C.red }}>
                {formatCurrency(totals.esi_employee)}
              </td>
              <td style={{ ...tdNumBold, color: C.red }}>
                {formatCurrency(totals.professional_tax)}
              </td>

              {/* Contributions (Employer) — NEW */}
              <td style={{ ...tdNumBold, color: C.teal, ...tdGroupStart }}>
                {formatCurrency(totals.epf_employer)}
              </td>
              <td style={{ ...tdNumBold, color: C.teal }}>
                {formatCurrency(totals.esi_employer)}
              </td>

              {/* Totals */}
              <td style={{ ...tdNumBold, color: C.green, ...tdGroupStart }}>
                {formatCurrency(totals.net)}
              </td>
              <td style={{ ...tdNumBold, color: C.purple }}>
                {totals.extra_pay > 0 ? formatCurrency(totals.extra_pay) : '—'}
              </td>
              <td style={{ ...tdNumBold, color: C.blue }}>
                {formatCurrency(totals.ctc)}
              </td>
            </tr>
          </tfoot>
        </table>

        <style>{`
        .salary-scroll tbody tr.salary-row:hover td {
          background-color: #f8fafc !important;
        }
      `}</style>
      </div>
    );
  };
  // ── render ──
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Monthly Salary" pageTitle="Salary Attendance" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="card-title mb-0">Monthly Salary</h5>
                  {status?.generated && (
                    <small className="text-muted">
                      {format(
                        new Date(selectedYear, selectedMonth - 1, 1),
                        'MMMM yyyy',
                      )}{' '}
                      <Badge color="info" className="ms-2">
                        {payslips.length} Payslips
                      </Badge>
                      <Badge color="secondary" className="ms-1">
                        <Clock size={10} className="me-1" />
                        {formatDateTime(status.last_generated_at)}
                      </Badge>
                    </small>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {status?.generated && (
                    <>
                      <Button
                        color="warning"
                        size="sm"
                        onClick={handleGenerate}
                        disabled={generating || loading}
                      >
                        {generating ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Regenerating…
                          </>
                        ) : (
                          <>
                            <RotateCw size={14} className="me-1" />
                            Regenerate
                          </>
                        )}
                      </Button>
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={exporting || loading || payslips.length === 0}
                      >
                        {exporting ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Exporting…
                          </>
                        ) : (
                          <>
                            <Download size={14} className="me-1" />
                            Export Excel
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={refresh}
                    disabled={loading || statusLoading}
                  >
                    <RefreshCw size={14} className="me-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="mb-3 p-3 border rounded bg-light">
                  <Row className="g-3 align-items-end">
                    <Col md={2}>
                      <FormGroup>
                        <Label className="form-label-sm">Month</Label>
                        <Input
                          type="select"
                          value={selectedMonth}
                          onChange={(e) =>
                            setSelectedMonth(parseInt(e.target.value))
                          }
                          bsSize="sm"
                        >
                          {monthOptions.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label className="form-label-sm">Year</Label>
                        <Input
                          type="select"
                          value={selectedYear}
                          onChange={(e) =>
                            setSelectedYear(parseInt(e.target.value))
                          }
                          bsSize="sm"
                        >
                          {yearOptions.map((y) => (
                            <option key={y.value} value={y.value}>
                              {y.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label className="form-label-sm">Designation</Label>
                        <Input
                          type="select"
                          value={filters.designation_id || ''}
                          onChange={(e) =>
                            handleFilterChange(
                              'designation_id',
                              e.target.value
                                ? parseInt(e.target.value)
                                : undefined,
                            )
                          }
                          bsSize="sm"
                        >
                          <option value="">All Designations</option>
                          {designations.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.title}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    {/* ✅ Company / Unit — replaces Employee Status */}
                    <Col md={3}>
                      <FormGroup>
                        <Label className="form-label-sm">Company</Label>
                        <Input
                          type="select"
                          value={filters.unit_id || ''}
                          onChange={(e) =>
                            handleFilterChange(
                              'unit_id',
                              e.target.value ? Number(e.target.value) : undefined,
                            )
                          }
                          bsSize="sm"
                        >
                          <option value="">All Companies</option>
                          {units.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md={2}>
                      <FormGroup>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            className="w-50"
                          >
                            Clear
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={refresh}
                            className="w-50"
                          >
                            Apply
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                {/* Tabs */}
                <Nav tabs className="mb-3">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === 'general' })}
                      onClick={() => setActiveTab('general')}
                      style={{ cursor: 'pointer' }}
                    >
                      <FileText size={16} className="me-1" /> General
                    </NavLink>
                  </NavItem>
                  {/* <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === 'combined' })}
                      onClick={() => setActiveTab('combined')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Layers size={16} className="me-1" /> Combined
                    </NavLink>
                  </NavItem> */}
                </Nav>

                {error && (
                  <Alert color="danger" className="mb-3">
                    <AlertCircle size={16} className="me-2" />
                    {error}
                  </Alert>
                )}

                {loading || statusLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" className="me-2" />
                    <span>{statusLoading ? 'Checking payslip status…' : 'Loading payslips…'}</span>
                  </div>
                ) : (
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="general">{renderTable()}</TabPane>
                    <TabPane tabId="combined">{renderTable()}</TabPane>
                  </TabContent>
                )}

                {status?.generated && payslips.length > 0 && (
                  <div className="mt-3">
                    <Alert color="info" className="mb-0">
                      <small>
                        <strong>Tip:</strong> Click on the{' '}
                        <span
                          className="fw-semibold"
                          style={{
                            backgroundColor: '#4b1ac9',
                            color: '#fff',
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                          }}
                        >
                          Emp Code
                        </span>{' '}
                        button to open the dropdown — from there you can:
                        <ul className="mb-0 mt-1 ps-3">
                          <li>
                            <strong>View Details</strong> — day-by-day attendance breakdown,
                            Sunday payments, extra pay, and LOP details
                          </li>
                          <li>
                            <strong>View Payslip</strong> — open the PDF in a new tab
                          </li>
                          <li>
                            <strong>Download Payslip</strong> — save the PDF to your device
                          </li>
                        </ul>
                      </small>
                    </Alert>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {/* ─── Floating Emp-Code dropdown (portal) ─── */}
      {payslipDropdownId !== null && dropdownRect && (() => {
        const p = payslips.find((x) => x.id === payslipDropdownId);
        if (!p) return null;

        // Position: below the button, left-aligned.
        // Flip if not enough room below.
        const MENU_WIDTH = 220;
        const MENU_HEIGHT_ESTIMATE = 180;
        const spaceBelow = window.innerHeight - dropdownRect.bottom;
        const openUpward = spaceBelow < MENU_HEIGHT_ESTIMATE;

        const top = openUpward
          ? dropdownRect.top - MENU_HEIGHT_ESTIMATE - 4
          : dropdownRect.bottom + 4;

        // Clamp left so the menu stays inside the viewport
        const left = Math.min(
          dropdownRect.left,
          window.innerWidth - MENU_WIDTH - 8,
        );

        return createPortal(
          <div
            data-payslip-dropdown
            style={{
              position: 'fixed',
              top,
              left,
              width: MENU_WIDTH,
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              zIndex: 9999,
              padding: '6px 0',
            }}
          >
            <button
              type="button"
              onClick={() => {
                closeDropdown();
                openDetail(p);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '9px 14px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                fontSize: '0.85rem',
                color: C.slate,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'none';
              }}
            >
              <Eye size={13} className="me-2" />
              View Details
            </button>

            <hr style={{ margin: '4px 0', borderColor: '#f1f5f9' }} />

            <button
              type="button"
              onClick={() => {
                closeDropdown();
                handlePayslipPdf(p.employee_id, p.year, p.month, 'view');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '9px 14px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                fontSize: '0.85rem',
                color: C.slate,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'none';
              }}
            >
              <FileText size={13} className="me-2" />
              View Payslip
            </button>

            <button
              type="button"
              onClick={() => {
                closeDropdown();
                handlePayslipPdf(p.employee_id, p.year, p.month, 'download');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '9px 14px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                fontSize: '0.85rem',
                color: C.slate,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'none';
              }}
            >
              <Download size={13} className="me-2" />
              Download Payslip
            </button>
          </div>,
          document.body,
        );
      })()}
      {/* ─────────────────── Payslip Detail Modal ─────────────────── */}
      <PayslipDetailModal
        isOpen={detailOpen}
        toggle={closeDetail}
        payslip={detailPayslip}
      />
      <LoadingOverlay
        show={generating || (loading && payslips.length === 0)}
        message={generating ? 'Generating payslips…' : 'Loading payslips…'}
        subMessage={
          generating
            ? 'This may take a few seconds'
            : 'Fetching data for the selected month'
        }
      />
      {/* ─────────────────── Missing Check-Out Modal ─────────────────── */}
      <Modal
        isOpen={missingModal}
        toggle={() => setMissingModal(false)}
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setMissingModal(false)}>
          <div className="d-flex align-items-center">
            <AlertCircle size={20} className="me-2 text-warning" />
            <div>
              <div className="fw-semibold">Attendance Warnings</div>
              <small className="text-muted">
                {missingRows.length} day{missingRows.length === 1 ? '' : 's'} need
                your attention
              </small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div
            className="mb-3 p-3 rounded"
            style={{
              background: '#fff8e1',
              border: '2px solid #ffc107',
            }}
          >
            <div className="d-flex align-items-start gap-2">
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div className="small">
                <div className="fw-bold mb-1" style={{ color: '#8b6000' }}>
                  Payslips were generated with warnings.
                </div>
                <div className="text-muted">
                  The following day(s) have a <strong>check-in without a check-out</strong>.
                  Payslips were created but may be inaccurate. Fix attendance and
                  regenerate.
                </div>
              </div>
            </div>
          </div>

          {missingRows.length > 0 ? (
            <div
              className="table-responsive"
              style={{ maxHeight: '50vh', overflow: 'auto' }}
            >
              <Table
                bordered
                hover
                size="sm"
                className="mb-0 align-middle"
                style={{ fontSize: '0.85rem' }}
              >
                <thead className="table-light sticky-top" style={{ zIndex: 5 }}>
                  <tr className="text-center">
                    <th style={{ width: '50px' }}>#</th>
                    <th style={{ width: '110px' }}>Emp Code</th>
                    <th>Employee</th>
                    <th style={{ width: '140px' }}>Date</th>
                    <th style={{ width: '110px' }}>Check In</th>
                    <th style={{ width: '110px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {missingRows.map((row, idx) => (
                    <tr key={`${row.employee_id}-${row.date}`}>
                      <td className="text-center">{idx + 1}</td>
                      <td className="text-center">
                        <Badge color="light" className="text-dark border">
                          {row.employee_code}
                        </Badge>
                      </td>
                      <td>
                        <span className="fw-semibold">{row.employee_name}</span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Calendar size={12} className="text-muted" />
                          <span>
                            {format(new Date(row.date), 'dd MMM yyyy')}
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        {row.check_in ? (
                          <span className="fw-semibold text-warning">
                            {formatTo12Hour(row.check_in)}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Badge color="warning" pill>
                          No Check-Out
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert color="warning" className="mb-0">
              No details provided by the server.
            </Alert>
          )}

          <Alert color="info" className="mt-3 mb-0">
            <small>
              <strong>How to fix:</strong> Go to the <strong>Attendance</strong> page,
              pick the same month, find the employee/day, and either add a check-out
              time (Manual Attendance) or mark the day as Half Day / Absent. Then
              click <strong>Regenerate</strong>.
            </small>
          </Alert>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setMissingModal(false)}>
            <X size={14} className="me-1" /> Close
          </Button>
          <Button
            color="warning"
            onClick={() => {
              const first = missingRows[0];
              const params = new URLSearchParams({
                year: String(selectedYear),
                month: String(selectedMonth),
              });
              if (first) {
                params.append('emp_id', String(first.employee_id));
                params.append('date', first.date);
              }
              window.location.href = `/attendance?${params.toString()}`;
            }}
          >
            <Clock size={14} className="me-1" /> Go to Attendance
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// ─────────────────────────── detail modal ───────────────────────────
interface PayslipDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  payslip: Payslip | null;
}

const PayslipDetailModal: React.FC<PayslipDetailModalProps> = ({
  isOpen,
  toggle,
  payslip,
}) => {
  if (!payslip) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Payslip</ModalHeader>
        <ModalBody>
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        </ModalBody>
      </Modal>
    );
  }

  const attendance: Record<string, AttendanceDayCell> =
    (payslip.breakdown as any) || {};

  const dayEntries = Object.entries(attendance)
    .map(([day, cell]) => ({ day: Number(day), cell }))
    .sort((a, b) => a.day - b.day);

  const monthLabel = format(
    new Date(payslip.year, payslip.month - 1, 1),
    'MMMM yyyy',
  );

  const getWeekday = (day: number): string => {
    try {
      const d = new Date(payslip.year, payslip.month - 1, day);
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
    } catch {
      return '';
    }
  };

  const pb = payslip.payable_breakdown;

  // ── Totals ──
  const grossSalary = toNum(payslip.gross_salary);
  const totalDeductions =
    toNum(payslip.epf_employee) +
    toNum(payslip.esi_employee) +
    toNum(payslip.professional_tax);

  const epfEmployer = toNum(payslip.epf_employer);
  const esiEmployer = toNum(payslip.esi_employer);
  const employerTotal = epfEmployer + esiEmployer;

  const ctc = toNum(payslip.ctc);
  const netSalary = toNum(payslip.net_salary);

  // ── Attendance summary ──
  const calendarDays = toNum(payslip.calendar_days);
  const workingDays = toNum(payslip.working_days);
  const daysWorked = toNum(payslip.days_worked);
  const extraDays = toNum(payslip.extra_days);
  const extraPay = toNum(payslip.extra_pay);
  const sundaysWorked = toNum(payslip.sundays_worked);
  const holidaysWorked = toNum(payslip.holidays_worked);
  const lopDays = toNum(payslip.lop_days);
  const absentDays = toNum(payslip.absent_days);

  // ── Earnings rows ──
  const earningsRows: Array<{ label: string; value: number; color?: string }> = [
    { label: 'Basic Salary (Earned)', value: toNum(payslip.basic_salary) },
    { label: 'House Rent Allowance', value: toNum(payslip.hra) },
    { label: 'Travel Allowance', value: toNum(payslip.travel_allowance) },
    { label: 'Special Allowance', value: toNum(payslip.special_allowance) },
    { label: 'Site Allowance', value: toNum(payslip.site_allowance) },
  ];

  if (extraPay > 0) {
    const daysText =
      extraDays > 0
        ? ` (${extraDays % 1 === 0 ? extraDays.toFixed(0) : extraDays.toFixed(1)} ${extraDays === 1 ? 'day' : 'days'
        })`
        : '';
    earningsRows.push({
      label: `Extra Pay${daysText}`,
      value: extraPay,
      color: '#16a34a',
    });
  }

  // ── Deduction rows (employee) ──
  const deductionsRows: Array<{ label: string; value: number }> = [];
  if (toNum(payslip.epf_employee) > 0) {
    deductionsRows.push({
      label: 'EPF (Employee)',
      value: toNum(payslip.epf_employee),
    });
  }
  if (toNum(payslip.esi_employee) > 0) {
    deductionsRows.push({
      label: 'ESI (Employee)',
      value: toNum(payslip.esi_employee),
    });
  }
  if (toNum(payslip.professional_tax) > 0) {
    deductionsRows.push({
      label: 'Professional Tax',
      value: toNum(payslip.professional_tax),
    });
  }
  if (deductionsRows.length === 0) {
    deductionsRows.push({ label: 'No Deductions', value: 0 });
  }

  // ── Employer contribution rows ──
  const employerRows: Array<{ label: string; value: number }> = [];
  if (epfEmployer > 0) {
    employerRows.push({
      label: `PF (Employer, ${payslip.rates_epf_employer_pct}%)`,
      value: epfEmployer,
    });
  }
  if (esiEmployer > 0) {
    employerRows.push({
      label: `ESI (Employer, ${payslip.rates_esi_employer_pct}%)`,
      value: esiEmployer,
    });
  }

  const ctcIncludesEmployee = ctc > grossSalary + employerTotal + 0.5;

  // ── Palette ──
  const C = {
    slate: '#0f172a',
    muted: '#64748b',
    border: '#e3e6ea',
    borderSoft: '#eef1f4',
    headBg: '#f1f5f9',
    green: '#15803d',
    red: '#b91c1c',
    blue: '#0b5ed7',
    purple: '#6d28d9',
    amber: '#b45309',
    teal: '#0e7490',
  };

  // ── Summary tile ──
  const Stat = ({
    label,
    value,
    color,
  }: {
    label: string;
    value: string | number;
    color?: string;
  }) => (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 0,
        padding: '10px 12px',
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        textAlign: 'left',
      }}
    >
      <div
        style={{
          fontSize: '0.65rem',
          fontWeight: 700,
          color: C.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1rem',
          fontWeight: 800,
          color: color ?? C.slate,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <FileText size={18} className="me-2" />
          <div>
            <div className="fw-semibold">
              {payslip.employee_name}
              <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                ({payslip.employee_code})
              </span>
            </div>
            <small className="text-muted">
              Payslip · {monthLabel} · {payslip.designation_title ?? '—'}
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* ═══════════════════════════════════════════════════════
            ATTENDANCE SUMMARY
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          Attendance Summary
        </h6>
        <div className="d-flex flex-wrap gap-2 mb-4">
          <Stat label="Calendar" value={calendarDays} />
          <Stat label="Working" value={workingDays} />
          <Stat label="Paid Days" value={daysWorked.toFixed(1)} color={C.blue} />
          <Stat
            label="Extra Days"
            value={extraDays > 0 ? extraDays.toFixed(1) : '—'}
            color={extraDays > 0 ? C.purple : undefined}
          />
          <Stat
            label="Sundays Worked"
            value={sundaysWorked > 0 ? sundaysWorked : '—'}
            color={sundaysWorked > 0 ? C.purple : undefined}
          />
          <Stat
            label="Holidays Worked"
            value={holidaysWorked > 0 ? holidaysWorked : '—'}
            color={holidaysWorked > 0 ? C.green : undefined}
          />
          <Stat
            label="LOP"
            value={lopDays > 0 ? lopDays : '—'}
            color={lopDays > 0 ? C.red : undefined}
          />
          <Stat
            label="Absent"
            value={absentDays > 0 ? absentDays : '—'}
            color={absentDays > 0 ? C.amber : undefined}
          />
        </div>

        {/* ═══════════════════════════════════════════════════════
            TABLE 1 — EARNINGS ONLY
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          Earnings
        </h6>

        <div
          className="rounded mb-4"
          style={{ border: `1px solid ${C.border}`, overflow: 'hidden' }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              <col style={{ width: '70%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>

            <thead>
              <tr style={{ background: C.headBg }}>
                <th
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: '#475569',
                    textTransform: 'uppercase',
                    borderRight: `1px solid ${C.border}`,
                  }}
                >
                  Component
                </th>
                <th
                  style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: '#475569',
                    textTransform: 'uppercase',
                  }}
                >
                  Amount (₹)
                </th>
              </tr>
            </thead>

            <tbody>
              {earningsRows.map((e, i) => (
                <tr
                  key={`e-${i}`}
                  style={{ borderTop: `1px solid ${C.borderSoft}` }}
                >
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.85rem',
                      color: e.color ?? C.slate,
                      borderRight: `1px solid ${C.border}`,
                    }}
                  >
                    {e.label}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.85rem',
                      textAlign: 'right',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: e.color ?? C.slate,
                    }}
                  >
                    {formatCurrency(e.value)}
                  </td>
                </tr>
              ))}

              {/* Gross salary total */}
              <tr
                style={{
                  background: '#f8fafc',
                  borderTop: '2px solid #cbd5e1',
                }}
              >
                <td
                  style={{
                    padding: '10px 12px',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRight: `1px solid ${C.border}`,
                  }}
                >
                  Gross Salary
                </td>
                <td
                  style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    fontVariantNumeric: 'tabular-nums',
                    color: C.slate,
                  }}
                >
                  {formatCurrency(grossSalary)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ═══════════════════════════════════════════════════════
            TABLE 2 — DEDUCTIONS (Employee) + CONTRIBUTIONS (Employer)
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          Deductions &amp; Employer Contributions
        </h6>

        <div
          className="rounded mb-4"
          style={{ border: `1px solid ${C.border}`, overflow: 'hidden' }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
            </colgroup>

            <thead>
              <tr style={{ background: C.headBg }}>
                {/* Deductions — 2 columns */}
                <th
                  colSpan={2}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: C.red,
                    textTransform: 'uppercase',
                    borderRight: `1px solid ${C.border}`,
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  Deductions (Employee)
                </th>
                {/* Contributions — 2 columns */}
                <th
                  colSpan={2}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: C.teal,
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  Contributions (Employer)
                </th>
              </tr>

              <tr style={{ background: C.headBg }}>
                <th
                  style={{
                    padding: '6px 12px',
                    textAlign: 'left',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: C.muted,
                    borderRight: `1px solid ${C.borderSoft}`,
                    // borderRight: `1px solid ${C.border}`,
                  }}
                >
                  Component
                </th>
                <th
                  style={{
                    padding: '6px 12px',
                    textAlign: 'right',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: C.muted,
                    borderRight: `1px solid ${C.border}`,
                  }}
                >
                  Amount
                </th>
                <th
                  style={{
                    padding: '6px 12px',
                    textAlign: 'left',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: C.muted,
                    borderRight: `1px solid ${C.borderSoft}`,
                  }}
                >
                  Component
                </th>
                <th
                  style={{
                    padding: '6px 12px',
                    textAlign: 'right',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: C.muted,
                  }}
                >
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              {(() => {
                const maxLen = Math.max(
                  deductionsRows.length,
                  employerRows.length,
                );
                const out: JSX.Element[] = [];

                for (let i = 0; i < maxLen; i++) {
                  const d = deductionsRows[i];
                  const er = employerRows[i];

                  out.push(
                    <tr
                      key={`ded-${i}`}
                      style={{ borderTop: `1px solid ${C.borderSoft}` }}
                    >
                      {/* Deduction label */}
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          color: d ? C.red : C.slate,
                          borderRight: `1px solid ${C.border}`,
                        }}
                      >
                        {d ? d.label : ''}
                      </td>
                      {/* Deduction value */}
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          textAlign: 'right',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                          color: d ? C.red : C.slate,
                          borderRight: `1px solid ${C.border}`,
                        }}
                      >
                        {d ? formatCurrency(d.value) : ''}
                      </td>

                      {/* Employer label */}
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          fontStyle: er ? 'italic' : 'normal',
                          color: er ? C.teal : C.slate,
                          borderRight: `1px solid ${C.borderSoft}`,
                        }}
                      >
                        {er ? er.label : ''}
                      </td>
                      {/* Employer value */}
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          textAlign: 'right',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                          fontStyle: er ? 'italic' : 'normal',
                          color: er ? C.teal : C.slate,
                        }}
                      >
                        {er ? formatCurrency(er.value) : ''}
                      </td>
                    </tr>,
                  );
                }

                // Totals row
                out.push(
                  <tr
                    key="ded-totals"
                    style={{
                      background: '#f8fafc',
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 12px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: C.red,
                        borderRight: `1px solid ${C.border}`,
                      }}
                    >
                      Total Deductions
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: C.red,
                        fontVariantNumeric: 'tabular-nums',
                        borderRight: `1px solid ${C.border}`,
                      }}
                    >
                      {formatCurrency(totalDeductions)}
                    </td>

                    <td
                      style={{
                        padding: '10px 12px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: C.teal,
                        borderRight: `1px solid ${C.borderSoft}`,
                      }}
                    >
                      Total Employer
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: C.teal,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatCurrency(employerTotal)}
                    </td>
                  </tr>,
                );

                return out;
              })()}
            </tbody>
          </table>
        </div>

        {/* ═══════════════════════════════════════════════════════
            CTC — DERIVATION STRIP
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          CTC Derivation
        </h6>
        <div
          className="rounded mb-4"
          style={{ border: `1px solid ${C.border}`, overflow: 'hidden' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: '0.82rem',
                    color: '#4b5563',
                  }}
                >
                  Gross Salary
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    textAlign: 'right',
                    fontSize: '0.82rem',
                    color: '#4b5563',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatCurrency(grossSalary)}
                </td>
              </tr>

              {epfEmployer > 0 && (
                <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.82rem',
                      fontStyle: 'italic',
                      color: C.teal,
                    }}
                  >
                    + PF (Employer, {payslip.rates_epf_employer_pct}%)
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontSize: '0.82rem',
                      fontStyle: 'italic',
                      color: C.teal,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCurrency(epfEmployer)}
                  </td>
                </tr>
              )}

              {esiEmployer > 0 && (
                <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.82rem',
                      fontStyle: 'italic',
                      color: C.teal,
                    }}
                  >
                    + ESI (Employer, {payslip.rates_esi_employer_pct}%)
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontSize: '0.82rem',
                      fontStyle: 'italic',
                      color: C.teal,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCurrency(esiEmployer)}
                  </td>
                </tr>
              )}

              {ctcIncludesEmployee && (
                <>
                  {toNum(payslip.epf_employee) > 0 && (
                    <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.82rem',
                          fontStyle: 'italic',
                          color: C.red,
                        }}
                      >
                        + PF (Employee, {payslip.rates_epf_employee_pct}%)
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          textAlign: 'right',
                          fontSize: '0.82rem',
                          fontStyle: 'italic',
                          color: C.red,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatCurrency(toNum(payslip.epf_employee))}
                      </td>
                    </tr>
                  )}

                  {toNum(payslip.esi_employee) > 0 && (
                    <tr style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                      <td
                        style={{
                          padding: '8px 12px',
                          fontSize: '0.82rem',
                          fontStyle: 'italic',
                          color: C.red,
                        }}
                      >
                        + ESI (Employee, {payslip.rates_esi_employee_pct}%)
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          textAlign: 'right',
                          fontSize: '0.82rem',
                          fontStyle: 'italic',
                          color: C.red,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatCurrency(toNum(payslip.esi_employee))}
                      </td>
                    </tr>
                  )}
                </>
              )}

              <tr
                style={{
                  borderTop: '2px solid #cbd5e1',
                  background: '#f8fafc',
                }}
              >
                <td
                  style={{
                    padding: '10px 12px',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: C.blue,
                  }}
                >
                  {ctcIncludesEmployee
                    ? 'CTC (Gross + Employer + Employee)'
                    : 'CTC (Gross + Employer)'}
                </td>
                <td
                  style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: C.blue,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatCurrency(ctc)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ═══════════════════════════════════════════════════════
            NET TAKE HOME
           ═══════════════════════════════════════════════════════ */}
        <div
          className="rounded d-flex justify-content-between align-items-center mb-4"
          style={{
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            padding: '14px 18px',
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#065f46',
              }}
            >
              Net Take Home Salary
            </div>
            <div style={{ fontSize: '0.72rem', color: '#059669' }}>
              = Gross − Total Deductions
            </div>
          </div>
          <span
            style={{
              fontWeight: 800,
              fontSize: '1.2rem',
              color: '#065f46',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(netSalary)}
          </span>
        </div>

        {/* ═══════════════════════════════════════════════════════
            OFFER VALUES
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          Offer Values (Reference)
        </h6>
        <div className="d-flex flex-wrap gap-2 mb-4">
          <Stat
            label="Take Home"
            value={formatCurrency(
              payslip.original_take_home_salary ?? payslip.take_home_salary,
            )}
          />
          <Stat
            label="Basic"
            value={formatCurrency(
              payslip.original_basic_salary ?? payslip.basic_salary,
            )}
          />
          <Stat
            label="Site"
            value={formatCurrency(payslip.original_site_allowance ?? 0)}
          />
          <Stat
            label="Per Day (basic)"
            value={formatCurrency(payslip.per_day_salary)}
          />
        </div>

        {/* ═══════════════════════════════════════════════════════
            DAY-BY-DAY ATTENDANCE
           ═══════════════════════════════════════════════════════ */}
        <h6 className="text-muted small text-uppercase mb-2">
          Day-by-Day Attendance
        </h6>

        {dayEntries.length === 0 ? (
          <Alert color="warning" className="mb-0">
            <AlertCircle size={16} className="me-2" />
            No day-by-day breakdown stored for this payslip. Regenerate to
            capture it.
          </Alert>
        ) : (
          <div
            className="table-responsive rounded"
            style={{
              maxHeight: '60vh',
              overflow: 'auto',
              border: `1px solid ${C.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Table
              bordered={false}
              hover
              size="sm"
              className="mb-0 align-middle payslip-days-table"
              style={{ fontSize: '0.82rem' }}
            >
              <thead
                className="sticky-top"
                style={{
                  zIndex: 5,
                  background:
                    'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
                  borderBottom: '2px solid #d0d7de',
                }}
              >
                <tr className="text-center align-middle">
                  <th style={{ width: 52, padding: '10px 6px' }}>Day</th>
                  <th style={{ width: 44, padding: '10px 6px' }}>Wk</th>
                  <th style={{ width: 130, padding: '10px 8px' }}>Status</th>
                  <th style={{ width: 88, padding: '10px 6px' }}>In</th>
                  <th style={{ width: 88, padding: '10px 6px' }}>Out</th>
                  <th style={{ width: 62, padding: '10px 6px' }}>Hrs</th>
                  <th style={{ width: 68, padding: '10px 6px' }}>Credit</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                    Notes
                  </th>
                </tr>
              </thead>

              <tbody>
                {dayEntries.map(({ day, cell }) => {
                  const isSunday =
                    new Date(
                      payslip.year,
                      payslip.month - 1,
                      day,
                    ).getDay() === 0;
                  const isHoliday = !!cell.is_holiday;
                  const isLop = !!cell.lop;
                  const isLeave = !!cell.is_leave;
                  const isPaidLeave = cell.is_paid_leave === true;
                  const isUnpaidLeave = isLeave && !isPaidLeave;
                  const isHalfDayLeave = isLeave && !!cell.is_half_day;

                  const weight = getDayWeight(
                    cell.status,
                    isSunday,
                    isHoliday,
                    isLop,
                    isLeave,
                    cell.is_paid_leave ?? null,
                    isHalfDayLeave,
                  );

                  let accent = '#ffffff';
                  let tint = 'transparent';
                  if (isLop) {
                    accent = '#8b0000';
                    tint = 'rgba(139, 0, 0, 0.06)';
                  } else if (isUnpaidLeave) {
                    accent = '#d97706';
                    tint = 'rgba(217, 119, 6, 0.06)';
                  } else if (isSunday) {
                    accent = '#94a3b8';
                    tint = 'rgba(148, 163, 184, 0.10)';
                  } else if (isHoliday) {
                    accent = '#0ea5e9';
                    tint = 'rgba(14, 165, 233, 0.07)';
                  } else if (isLeave) {
                    accent = '#0ea5e9';
                    tint = 'rgba(14, 165, 233, 0.05)';
                  }

                  const weightColor = isLop
                    ? 'danger'
                    : isUnpaidLeave
                      ? 'warning'
                      : isLeave
                        ? isPaidLeave
                          ? 'info'
                          : 'warning'
                        : weight === 0
                          ? 'light'
                          : weight === 1
                            ? isSunday || isHoliday
                              ? 'primary'
                              : 'success'
                            : 'warning';

                  return (
                    <tr
                      key={day}
                      style={{
                        background: tint,
                        borderBottom: `1px solid ${C.borderSoft}`,
                      }}
                    >
                      <td
                        className="text-center fw-semibold"
                        style={{
                          borderLeft: `4px solid ${accent}`,
                          padding: '10px 6px',
                          color: C.slate,
                        }}
                      >
                        {day}
                      </td>

                      <td
                        className="text-center"
                        style={{ color: '#6b7280', padding: '10px 6px' }}
                      >
                        {getWeekday(day)}
                      </td>

                      <td className="text-center" style={{ padding: '10px 8px' }}>
                        {isLop ? (
                          <span
                            style={{
                              display: 'inline-block',
                              backgroundColor: '#8b0000',
                              color: '#fff',
                              border: '1.5px solid #ffd700',
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              padding: '3px 10px',
                              borderRadius: 999,
                              letterSpacing: '0.03em',
                            }}
                          >
                            LOP
                          </span>
                        ) : isLeave ? (
                          <span
                            style={{
                              display: 'inline-block',
                              backgroundColor: isPaidLeave
                                ? '#e0f2fe'
                                : '#fef3c7',
                              color: isPaidLeave ? '#075985' : '#92400e',
                              border: `1px solid ${isPaidLeave ? '#7dd3fc' : '#fcd34d'
                                }`,
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              padding: '3px 10px',
                              borderRadius: 999,
                            }}
                          >
                            {isPaidLeave ? '🏖️' : '⚠️'}{' '}
                            {cell.leave_type_name ?? 'Leave'}
                            {isHalfDayLeave ? ' ½' : ''}
                          </span>
                        ) : (
                          <Badge
                            color={getStatusColor(cell.status)}
                            pill
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '4px 10px',
                            }}
                          >
                            {getStatusLabel(cell.status)}
                          </Badge>
                        )}
                      </td>

                      <td
                        className="text-center"
                        style={{
                          padding: '10px 6px',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, monospace',
                          fontSize: '0.78rem',
                          color: cell.check_in ? C.slate : '#cbd5e1',
                        }}
                      >
                        {cell.check_in ? formatTo12Hour(cell.check_in) : '—'}
                      </td>

                      <td
                        className="text-center"
                        style={{
                          padding: '10px 6px',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, monospace',
                          fontSize: '0.78rem',
                          color: cell.check_out ? C.slate : '#cbd5e1',
                        }}
                      >
                        {cell.check_out ? formatTo12Hour(cell.check_out) : '—'}
                      </td>

                      <td
                        className="text-center"
                        style={{
                          padding: '10px 6px',
                          fontWeight: 600,
                          color:
                            cell.hours != null && cell.hours > 0
                              ? C.slate
                              : '#cbd5e1',
                        }}
                      >
                        {cell.hours != null && cell.hours > 0
                          ? cell.hours.toFixed(1)
                          : '—'}
                      </td>

                      <td className="text-center" style={{ padding: '10px 6px' }}>
                        {weight === 0 ? (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        ) : (
                          <Badge
                            color={weightColor}
                            pill
                            style={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              padding: '4px 10px',
                              minWidth: 42,
                            }}
                          >
                            {weight.toFixed(1)}
                          </Badge>
                        )}
                      </td>

                      <td style={{ padding: '10px 14px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            fontSize: '0.76rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {isLop && cell.lop && (
                            <div
                              style={{
                                color: '#8b0000',
                                fontWeight: 600,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              🚫 <strong>LOP:</strong> {cell.lop.notes}
                            </div>
                          )}

                          {isLeave && (
                            <div
                              style={{
                                color: isPaidLeave ? '#075985' : '#92400e',
                                fontWeight: 500,
                              }}
                            >
                              {isPaidLeave ? '🏖️' : '⚠️'}{' '}
                              <strong>
                                {cell.leave_type_name ?? 'Leave'}
                              </strong>
                              {isHalfDayLeave ? ' (Half Day)' : ''}
                              {' · '}
                              <span style={{ fontWeight: 600 }}>
                                {isPaidLeave ? 'Paid' : 'Unpaid'}
                              </span>
                              {cell.leave_reason && (
                                <>
                                  {' · '}
                                  <em style={{ color: '#6b7280' }}>
                                    {cell.leave_reason}
                                  </em>
                                </>
                              )}
                            </div>
                          )}

                          {isHoliday && cell.holiday_name && (
                            <div
                              style={{ color: '#0369a1', fontWeight: 500 }}
                            >
                              🎉 <strong>{cell.holiday_name}</strong>
                              {cell.hours && cell.hours > 0 && (
                                <span
                                  className="ms-2"
                                  style={{ color: '#16a34a', fontWeight: 600 }}
                                >
                                  · Worked ({cell.hours.toFixed(1)} hrs)
                                </span>
                              )}
                            </div>
                          )}

                          {isSunday &&
                            !cell.check_in &&
                            !isLeave &&
                            !isLop && (
                              <div style={{ color: '#64748b' }}>
                                💤 Weekly Off
                              </div>
                            )}

                          {isSunday && cell.hours && cell.hours > 0 && (
                            <div
                              style={{ color: '#16a34a', fontWeight: 600 }}
                            >
                              ⭐ Worked on Sunday ({cell.hours.toFixed(1)} hrs)
                            </div>
                          )}

                          {cell.check_in_notes && (
                            <div style={{ color: '#6b7280' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  minWidth: 26,
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                }}
                              >
                                IN
                              </span>
                              {cell.check_in_notes}
                            </div>
                          )}

                          {cell.check_out_notes &&
                            cell.check_out_notes !== cell.check_in_notes && (
                              <div style={{ color: '#6b7280' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    minWidth: 26,
                                    color: '#9ca3af',
                                    fontWeight: 600,
                                  }}
                                >
                                  OUT
                                </span>
                                {cell.check_out_notes}
                              </div>
                            )}

                          {!isLop &&
                            !isLeave &&
                            !isHoliday &&
                            !isSunday &&
                            !cell.check_in_notes &&
                            !cell.check_out_notes && (
                              <span style={{ color: '#cbd5e1' }}>—</span>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot
                style={{
                  background:
                    'linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%)',
                  borderTop: '2px solid #cbd5e1',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 5,
                }}
              >
                <tr className="text-center align-middle fw-semibold">
                  <td
                    colSpan={5}
                    className="text-end"
                    style={{ padding: '12px 12px' }}
                  >
                    TOTAL
                  </td>
                  <td
                    style={{
                      padding: '12px 6px',
                      fontWeight: 700,
                      color: C.slate,
                    }}
                  >
                    {dayEntries
                      .reduce((sum, { cell }) => sum + (cell.hours || 0), 0)
                      .toFixed(1)}
                  </td>
                  <td style={{ padding: '12px 6px' }}>
                    <Badge
                      color="dark"
                      pill
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        padding: '5px 12px',
                      }}
                    >
                      {daysWorked.toFixed(1)}
                    </Badge>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <X size={14} className="me-1" /> Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
// ─────────────────────────── building blocks ───────────────────────────
const SummaryTile: React.FC<{
  label: React.ReactNode;
  value: string;
  /**
   * Optional accent color. When provided, only the value text is tinted
   * and a small left border is drawn. The label stays muted dark gray.
   * Use hex colors for full control (avoid Bootstrap's light tones).
   */
  accent?: string;
}> = ({ label, value, accent }) => (
  <div
    className="border rounded p-2 bg-white h-100"
    style={{
      borderColor: '#e5e7eb',
      borderLeft: accent ? `3px solid ${accent}` : undefined,
    }}
  >
    <small
      className="d-block"
      style={{ color: '#6b7280', fontSize: '0.72rem' }}
    >
      {label}
    </small>
    <div
      className="fw-semibold"
      style={{ color: accent || '#111827', fontSize: '0.95rem' }}
    >
      {value}
    </div>
  </div>
);

// ─────────────────────────── loading overlay ───────────────────────────
const LoadingOverlay: React.FC<{
  show: boolean;
  message?: string;
  subMessage?: string;
}> = ({ show, message = 'Loading…', subMessage }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px 40px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 240,
        }}
      >
        <Spinner
          color="primary"
          style={{ width: '2.5rem', height: '2.5rem', borderWidth: '0.25em' }}
        />
        <div className="fw-semibold mt-3" style={{ color: '#0656A8' }}>
          {message}
        </div>
        {subMessage && (
          <small className="text-muted mt-1 text-center">{subMessage}</small>
        )}
      </div>
    </div>
  );
};
export default MonthlySalary;